// Accordion component
// Used in footer and mobile navigation drawer

class AccordionTrigger extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.button = null;
    this.content = null;
    this.resizeObserver = null;
    this.animation = null;
  }

  connectedCallback() {
    this.button = this.querySelector('button');

    if (!this.button) {
      console.error('AccordionTrigger: button element is required inside accordion-trigger');
      return;
    }

    const controlsId = this.button.getAttribute('aria-controls');
    if (!controlsId) {
      console.warn('AccordionTrigger: aria-controls is required on the button');
      return;
    }

    this.content = this.nextElementSibling || document.getElementById(controlsId);

    if (!this.content) {
      console.warn(`AccordionTrigger: content with id "${controlsId}" not found`);
      return;
    }

    this.button.addEventListener('click', this.handleClick);
    window.addEventListener('resize', this.handleResize);
    this.setupContent();
    this.syncState({ immediate: true });

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isExpanded()) {
        this.content.style.height = 'auto';
      }
    });
    this.resizeObserver.observe(this.content);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.cancelAnimation();
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  handleResize() {
    if (!this.content) return;

    if (this.isFooterAccordion()) {
      this.syncState({ immediate: true });
      return;
    }

    if (this.isExpanded()) {
      this.content.style.height = 'auto';
    }
  }

  isExpanded() {
    return this.button?.getAttribute('aria-expanded') === 'true';
  }

  isFooterAccordion() {
    return this.button?.getAttribute('id')?.includes('footer');
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  setupContent() {
    this.content.style.overflow = 'hidden';
    this.content.style.willChange = 'height';
  }

  syncState({ immediate = false } = {}) {
    const shouldExpand = this.isFooterAccordion() ? window.innerWidth >= 1024 : this.isExpanded();
    this.button.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');

    if (immediate || this.prefersReducedMotion()) {
      this.cancelAnimation();
      this.content.setAttribute('data-active', shouldExpand ? 'true' : 'false');
      this.content.style.height = shouldExpand ? 'auto' : '0px';
      return;
    }

    this.animateContent(shouldExpand);
  }

  toggle() {
    if (!this.content) return;

    const newState = !this.isExpanded();
    this.button.setAttribute('aria-expanded', newState ? 'true' : 'false');
    this.animateContent(newState);
  }

  cancelAnimation() {
    if (!this.animation) return;
    this.animation.cancel();
    this.animation = null;
  }

  animateContent(shouldExpand) {
    if (!this.content) return;

    if (this.prefersReducedMotion()) {
      this.syncState({ immediate: true });
      return;
    }

    const content = this.content;
    const startHeight = content.getBoundingClientRect().height;
    const endHeight = shouldExpand ? content.scrollHeight : 0;

    this.cancelAnimation();

    if (shouldExpand) {
      content.setAttribute('data-active', 'true');
    }

    content.style.height = `${startHeight}px`;

    if (startHeight === endHeight) {
      content.style.height = shouldExpand ? 'auto' : '0px';
      content.setAttribute('data-active', shouldExpand ? 'true' : 'false');
      return;
    }

    this.animation = content.animate(
      [
        { height: `${startHeight}px` },
        { height: `${endHeight}px` },
      ],
      {
        duration: 300,
        easing: 'ease',
        fill: 'forwards',
      }
    );

    this.animation.onfinish = () => {
      if (shouldExpand) {
        content.style.height = 'auto';
        content.setAttribute('data-active', 'true');
      } else {
        content.style.height = '0px';
        content.setAttribute('data-active', 'false');
      }
      const finished = this.animation;
      this.animation = null;
      if (finished) finished.cancel();
    };

    this.animation.oncancel = () => {
      this.animation = null;
    };
  }
}

// Register accordion component
customElements.define('accordion-trigger', AccordionTrigger);

// Read more / read less toggle for richtext blocks
class ReadMoreBlock extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.btn = this.querySelector('[data-read-more-label]');

    if (!this.content || !this.btn) return;

    this.readMoreLabel = this.btn.dataset.readMoreLabel;
    this.readLessLabel = this.btn.dataset.readLessLabel;
    this.handleClick = this.handleClick.bind(this);
    this.checkOverflow = this.checkOverflow.bind(this);
    this.btn.addEventListener('click', this.handleClick);

    this.checkOverflow();
    this.resizeObserver = new ResizeObserver(this.checkOverflow);
    this.resizeObserver.observe(this.content);
  }

  disconnectedCallback() {
    if (this.btn) {
      this.btn.removeEventListener('click', this.handleClick);
    }
    if (this.resizeObserver && this.content) {
      this.resizeObserver.unobserve(this.content);
    }
  }

  checkOverflow() {
    if (!this.content || !this.btn) return;

    requestAnimationFrame(() => {
      const isExpanded = this.content.classList.contains('is-expanded');
      const overflows = isExpanded || this.content.scrollHeight > this.content.clientHeight;
      this.btn.setAttribute('aria-hidden', overflows ? 'false' : 'true');
    });
  }

  handleClick() {
    const isExpanded = this.btn.getAttribute('aria-expanded') === 'true';
    this.content.classList.toggle('is-expanded', !isExpanded);
    this.btn.setAttribute('aria-expanded', String(!isExpanded));
    this.btn.textContent = isExpanded ? this.readMoreLabel : this.readLessLabel;
    this.checkOverflow();
  }
}

customElements.define('read-more-block', ReadMoreBlock);

// Mobile sticky Add to Cart bar
// Reveals itself only while the in-page product form is off-screen AND the footer isn't in view.
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const section = this.closest('section') || document;
    this.target = section.querySelector('[data-atc-observe]');
    this.footer = document.querySelector('footer');

    if (!this.target) return;

    // Without IntersectionObserver, keep the bar available rather than stuck hidden.
    if (!('IntersectionObserver' in window)) {
      this.setVisible(true);
      return;
    }

    this.formVisible = true;
    this.footerVisible = false;
    this.observers = [];

    const formObserver = new IntersectionObserver((entries) => {
      this.formVisible = entries[0].isIntersecting;
      this.update();
    }, { threshold: 0 });
    formObserver.observe(this.target);
    this.observers.push(formObserver);

    if (this.footer) {
      const footerObserver = new IntersectionObserver((entries) => {
        this.footerVisible = entries[0].isIntersecting;
        this.update();
      }, { threshold: 0 });
      footerObserver.observe(this.footer);
      this.observers.push(footerObserver);
    }
  }

  disconnectedCallback() {
    if (this.observers) {
      this.observers.forEach((observer) => observer.disconnect());
      this.observers = null;
    }
  }

  update() {
    this.setVisible(!this.formVisible && !this.footerVisible);
  }

  setVisible(visible) {
    this.classList.toggle('translate-y-full', !visible);
    this.classList.toggle('pointer-events-none', !visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
}

if (!customElements.get('rc-sticky-atc')) customElements.define('rc-sticky-atc', StickyAtc);
